import { PlatformLocation } from '@angular/common';
import { HttpProgressEvent } from '@angular/common/http';
import { GraphIdentitySet } from '@fluidx/utilities/lib/graph/graph';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise, InstrumentedHttpPromiseCallback } from '../instrumentation/instrumented-http.service';
import { BatchRequestItem } from '../models/batch-request-item.model';
import { GraphBatchRequest, GraphBatchRequestItem, GraphBatchResponse, GraphBatchResponseItem, GraphError } from '../models/graph.model';
import { HttpRequestMethod } from '../models/http-request-method.enum';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { ContextService } from '../shared-context/context.service';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { AccessTokenConsumer, AccessTokenService, CAPDialogData } from './access-token.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { Performance } from './performance.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { XhrService, XhrServiceError } from './xhr.service';

class BatchError {
}

class GraphResponseCache {
    private readonly cachedResponsePromises = new Map<string, Promise<InstrumentedHttpDataResponse<any>>>();

    public set(responsePromise: Promise<InstrumentedHttpDataResponse<any>>, endpoint: string, beta: boolean, strategy: FetchStrategy) {
        const key = this.key(endpoint, beta, strategy);
        this.cachedResponsePromises.set(key, responsePromise);
    }

    public has(endpoint: string, beta: boolean, strategy: FetchStrategy): boolean {
        const key = this.key(endpoint, beta, strategy);
        return this.cachedResponsePromises.has(key);
    }

    public get(endpoint: string, beta: boolean, strategy: FetchStrategy): Promise<InstrumentedHttpDataResponse<any>> {
        const key = this.key(endpoint, beta, strategy);
        return this.cachedResponsePromises.get(key);
    }

    public delete(endpoint: string, beta: boolean, strategy: FetchStrategy): boolean {
        const key = this.key(endpoint, beta, strategy);
        return this.cachedResponsePromises.delete(key);
    }

    private key(endpoint: string, beta: boolean, strategy: FetchStrategy): string {
        return `${beta ? 'beta' : 'v1.0'}/${strategy}/${endpoint}`;
    }
}

export interface ListItemVersion {
    id: string;
    lastModified: GraphIdentitySet;
    lastModifiedDateTime: string;
}

/**
 * Abstract service that hits the Graph API.
 */
export abstract class GraphService extends XhrService {
    public static readonly graphV1 = 'v1.0';
    public static readonly graphBeta = 'beta';

    private static readonly graphBatchEndpoint = '$batch';
    private static readonly mockGraphHeader = 'ohp-mock-graph';

    private readonly graphServiceHost: string;
    protected requestConfig: HttpRequestOptions;

    private readonly cachedResponsePromises = new GraphResponseCache();

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        protected globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Graph, contextService, officeHomeApiConfigService, platformLocation, scheduler);

        const { officeHomeApiRoot, graphApiRoot } = contextService.sharedContext;

        // If we are running in test mode then the host will be set to officeHomeApiRoot so that we handle the request in OfficeHome app.
        this.graphServiceHost = this.inMockMode ? officeHomeApiRoot : graphApiRoot;
        this.requestConfig = { headers: new Map() };
    }

    protected getGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        strategy: FetchStrategy,
        instrumentCallback?: InstrumentedHttpPromiseCallback<T | GraphError>,
        cacheResult = false,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        beta: boolean = false,
        responseType?: HttpResponseContentType,
        schedulePriority: SchedulePriority = null,
        bypassMemoryCache = false
    ): Promise<InstrumentedHttpDataResponse<T>> {
        if (!bypassMemoryCache && this.cachedResponsePromises.has(endpoint, beta, strategy)) {
            return this.cachedResponsePromises.get(endpoint, beta, strategy);
        }

        const getPromise = new Promise<InstrumentedHttpDataResponse<T>>((resolve, reject) => {
            const url = this.getGraphServiceEndpoint(endpoint, beta);
            if (!url) {
                reject(new XhrServiceError(null, 'Graph service endpoint url is not defined'));
            }

            const requestConfig = this.getRequestConfig();

            if (typeof responseType !== 'undefined') {
                requestConfig.responseType = responseType;
            } else {
                requestConfig.responseType = HttpResponseContentType.Json;
            }

            const errorCallback = (error: XhrServiceError<T | GraphError>) => {
                if (cacheResult) {
                    this.cachedResponsePromises.delete(endpoint, beta, strategy);
                }

                reject(error);
            };

            const callback = (promise: InstrumentedHttpPromise<T | GraphError>) => {
                promise
                    .instrument((result, event) => {
                        event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        if (instrumentCallback) {
                            instrumentCallback(result, event);
                        }
                    })
                    .then(response => resolve(response as InstrumentedHttpDataResponse<T>))
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.XDHsY);
                    });
            };
            super.get(
                eventName,
                url,
                callback,
                errorCallback,
                requestConfig,
                strategy,
                onDownloadProgress,
                cancellationToken,
                timeoutInMs,
                undefined,
                undefined,
                schedulePriority
            );
        });

        if (cacheResult) {
            this.cachedResponsePromises.set(getPromise, endpoint, beta, strategy);
        }

        return getPromise;
    }

    protected postGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        beta: boolean = false,
        allowInteractive: boolean = false,
        capDialogData: CAPDialogData = null,
        specialHeaders: Map<string, string> = undefined
    ) {
        const url = this.getGraphServiceEndpoint(endpoint, beta);
        const requestConfig = this.getRequestConfig();
        if (specialHeaders !== undefined) {
            specialHeaders.forEach((value: string, key: string) => {
                requestConfig.headers.set(key, value);
            });
        }

        return super.post(eventName, url, data, callback, errorCallback, requestConfig, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs, undefined, null, null, null, allowInteractive, capDialogData);
    }

    protected putGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        beta: boolean = false
    ) {
        const url = this.getGraphServiceEndpoint(endpoint, beta);
        return super.put(eventName, url, data, callback, errorCallback, this.getRequestConfig(), onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs);
    }

    protected batchGraph<T>(
        requests: Array<BatchRequestItem<T>>,
        batchEventName?: KnownInstrumentationEvents
    ): Promise<Array<InstrumentedHttpDataResponse<T | GraphError>>> {
        const maxBatchSize = this.contextService.sharedContext.graphMaxBatchSize;
        const promiseArray: Array<Promise<Array<InstrumentedHttpDataResponse<T | GraphError>>>> = [];
        for (let i = 0; i < requests.length; i += 5) {
            promiseArray.push(this.batchGraphMaxSize(requests.slice(i, i + maxBatchSize), batchEventName));
        }
        return Promise.all(promiseArray).then(data => [].concat(...data));
    }

    protected patchGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig?: HttpRequestOptions,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string,
        beta: boolean = false
    ) {
        const url = this.getGraphServiceEndpoint(endpoint, beta);
        const patchRequestConfig = !!requestConfig ? requestConfig : this.getRequestConfig();
        return super.patch(eventName, url, data, callback, errorCallback, patchRequestConfig, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs, tokenConsumerOverride, resourceUrl);
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockgraph') {
            requestConfig?.headers.set(GraphService.mockGraphHeader, value);
        }
    }

    protected getRequestConfig(overrides: HttpRequestOptions = {}): HttpRequestOptions {
        return {
            ...this.requestConfig,
            headers: new Map(this.requestConfig.headers),
            ...overrides
        };
    }

    private batchGraphMaxSize<T>(
        requests: Array<BatchRequestItem<T>>,
        batchEventName?: KnownInstrumentationEvents
    ): Promise<Array<InstrumentedHttpDataResponse<T | GraphError>>> {
        const maxBatchSize = this.contextService.sharedContext.graphMaxBatchSize;
        if (requests.length > maxBatchSize) {
            return Promise.reject<Array<InstrumentedHttpDataResponse<T | GraphError>>>(new XhrServiceError(null, `Batch requests limited to ${maxBatchSize} items`));
        }

        const strategy = FetchStrategy.NetworkOnly;

        const shouldCache = (request: BatchRequestItem<T>) => request.cacheResult && request.method === HttpRequestMethod.Get;

        const uncachedRequests = requests.filter(request => !shouldCache(request) || !this.cachedResponsePromises.has(request.endpoint, true, strategy));

        const batchPromise = this.batchGraphUncached(uncachedRequests, batchEventName);
        let batchIndex = 0;
        const responsePromises: Array<Promise<InstrumentedHttpDataResponse<T | GraphError>>> = requests.map(request => {
            const cache = shouldCache(request);
            if (cache && this.cachedResponsePromises.has(request.endpoint, true, strategy)) {
                return this.cachedResponsePromises.get(request.endpoint, true, strategy);
            }
            const responseIndex = batchIndex;
            const itemPromise = batchPromise
                .then(batch => {
                    if (batch && batch.length > responseIndex) {
                        const response = batch[responseIndex];
                        if (cache && (!response || !response.ok)) {
                            this.cachedResponsePromises.delete(request.endpoint, true, strategy);
                        }

                        return response;
                    } else {
                        throw new BatchError();
                    }
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    if (!(error instanceof BatchError)) {
                        this.globalErrorHandler.handleError(error, ErrorId.ioc6B);
                    }

                    if (cache) {
                        this.cachedResponsePromises.delete(request.endpoint, true, strategy);
                    }

                    return null;
                });

            if (cache) {
                this.cachedResponsePromises.set(itemPromise, request.endpoint, true, strategy);
            }

            batchIndex++;
            return itemPromise;
        });

        return Promise.all(responsePromises);
    }

    // TODO: Enable incremental response handling and v1.0 when available
    private batchGraphUncached<T>(
        requests: Array<BatchRequestItem<T>>,
        batchEventName: KnownInstrumentationEvents
    ): Promise<Array<InstrumentedHttpDataResponse<T | GraphError>>> {
        return new Promise<Array<InstrumentedHttpDataResponse<T | GraphError>>>((resolve, reject) => {
            const eventName = batchEventName || KnownInstrumentationEvents.GraphBatchRequest;
            const url = this.getGraphServiceEndpoint(GraphService.graphBatchEndpoint, true);

            const requestItems: Array<GraphBatchRequestItem> = requests.map((request, index) => {
                let requestHeaders = undefined;
                if (request.headers) {
                    requestHeaders = {};
                    request.headers.forEach((value, key) => requestHeaders[key] = value);
                }
                return {
                    id: index,
                    method: request.method.toUpperCase(),
                    url: request.endpoint,
                    headers: requestHeaders,
                    body: request.body
                };
            });

            const batchSize = requestItems.length;
            const batch: GraphBatchRequest = {
                requests: requestItems
            };

            const errorCallback = (error: XhrServiceError<GraphBatchResponse<T | GraphError> | GraphError>) => {
                reject(error);
            };

            const batchId = GuidGenerator.createGuid();
            const start = Performance.now();

            const callback = (promise: InstrumentedHttpPromise<GraphBatchResponse<T | GraphError> | GraphError>, accumulatedResponse: Array<GraphBatchResponseItem<T | GraphError>>, attempt: number) => {
                promise
                    .instrument((result, event) => {
                        event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        event.params[InstrumentationParam.Index] = attempt.toString();
                        event.params[InstrumentationParam.BatchId] = batchId;
                        event.params[InstrumentationParam.BatchSize] = batchSize.toString();
                        if (result.ok) {
                            const data = result.data as GraphBatchResponse<T | GraphError>;
                            const responses = data.responses || [];
                            const responseCount = responses.length;
                            const successCount = responses.filter(response => response.status >= 200 && response.status < 300).length;
                            event.params[InstrumentationParam.ItemCount] = responseCount.toString();
                            event.params[InstrumentationParam.SuccessCount] = successCount.toString();
                            event.params[InstrumentationParam.ErrorCount] = (responseCount - successCount).toString();
                        }
                    })
                    .then(response => {
                        const data = response.data as GraphBatchResponse<T | GraphError>;
                        if (data && (data.responses || data.nextLink)) {
                            const responses = data.responses || [];
                            const batchResponses = accumulatedResponse.concat(responses);
                            // TODO: Uncomment and verify nextLink handling when it is added to the API
                            const latency = Math.ceil(Performance.now() - start);
                            const orderedResponses = this.orderAndInstrumentBatchResponses(requests, batchResponses, response.config, response.fromCache, batchId, batchSize, latency);
                            resolve(orderedResponses);
                            //if (!data.nextLink) {
                            //    const latency = Math.ceil(Performance.now() - start);
                            //    const orderedResponses = this.orderAndInstrumentBatchResponses(requests, batchResponses, response.config, batchId, batchSize, latency);
                            //    resolve(orderedResponses);
                            //} else {
                            //    super.post(eventName, data.nextLink, batch, (promise) => callback(promise, batchResponses, attempt + 1), errorCallback, this.requestConfig);
                            //}
                        } else {
                            reject(new XhrServiceError(response, 'Could not parse graph batch response'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.wFM08);
                    });
            };

            super.post(eventName,
                url,
                batch,
                promise => callback(promise, [], 1),
                errorCallback,
                this.getRequestConfig()
            );
        });
    }

    private orderAndInstrumentBatchResponses<T>(
        requests: Array<BatchRequestItem<T>>,
        batchResponses: Array<GraphBatchResponseItem<T | GraphError>>,
        responseConfig: any,
        fromCache: boolean,
        batchId: string,
        batchSize: number,
        latency: number
    ): Array<InstrumentedHttpDataResponse<T | GraphError>> {
        return requests.map((req, index) => {
            const res = batchResponses.find(r => r.id === index);

            const statusText = res.status.toString();
            const instrumentedResponse = res ? {
                data: res.body,
                status: res.status,
                headers: res.headers,
                config: responseConfig,
                ok: res.status >= 200 && res.status < 300,
                statusText,
                fromCache
            } : null;

            const instrumentationEvent = this.instrumentationService.generateRequestEvent(
                `${req.eventName}_Batched` as KnownInstrumentationEvents, // TODO: Create a list of batch requests made and place into KnownInstrumentationEvent enum instead of typecasting
                res ? res.status : HttpStatusCode.UnexpectedResponse,
                latency,
                !instrumentedResponse ? 'Response missing from batch' : null,
                instrumentedResponse.fromCache);

            instrumentationEvent.params[InstrumentationParam.BatchId] = batchId;
            instrumentationEvent.params[InstrumentationParam.BatchSize] = batchSize.toString();
            instrumentationEvent.params[InstrumentationParam.Index] = index.toString();

            // Post-process event before recording if successful
            if (req.instrumentCallback && instrumentedResponse && instrumentedResponse.ok) {
                req.instrumentCallback(instrumentedResponse as InstrumentedHttpDataResponse<T>, instrumentationEvent);
            }

            instrumentationEvent.record();

            return instrumentedResponse;
        });
    }

    private getGraphServiceEndpoint(endpoint: string, beta = false): string {
        return this.graphServiceHost ? `${this.graphServiceHost}/${beta ? GraphService.graphBeta : GraphService.graphV1}/${endpoint}` : '';
    }
}
